<script setup lang="ts">
import useLocale from '../../composables/useLocale';
import { useDisplay } from 'vuetify';
import { watch, ref } from 'vue';
import { storeToRefs } from 'pinia';
import useAudioAnnotations from '../../composables/use-audio-annotations';
import useMediaStore from '../../stores/media';
import useSettingStore from '../../stores/setting';
import useUiStore from '../../stores/ui';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTextSize } from '@fortawesome/pro-solid-svg-icons';
library.add(faTextSize);

type Emits = {
  (e: 'update:modelValue', value: number): void;
};
const emit = defineEmits<Emits>();

type Props = {
  baseFontSize: number;
};
const props = defineProps<Props>();

const fontSize = defineModel<number>({ required: true });

const { t } = useLocale();
const { smAndDown } = useDisplay();
const mediaStore = useMediaStore();
const { currentTrack, isPlaying } = storeToRefs(mediaStore);
const { hasAudioTracking } = storeToRefs(useSettingStore());
const { isReaderVisible } = storeToRefs(useUiStore());
const { highlightPlayingTrack, clearPlayingHighlight } = useAudioAnnotations();

// Local % value shown in UI (clamped 20–200)
const fontSizePct = ref(100);

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

// Sync from fontSize → percentage
watch(
  fontSize,
  (newVal, oldVal) => {
    const pct = (newVal / props.baseFontSize) * 100;
    fontSizePct.value = Math.round(clamp(pct, 20, 200));

    // Refresh audio highlight when font size actually changes (not initial load)
    if (
      oldVal !== undefined &&
      newVal !== oldVal &&
      currentTrack.value &&
      hasAudioTracking.value &&
      isReaderVisible.value &&
      isPlaying.value
    ) {
      console.debug('Font size changed, refreshing audio highlight');
      clearPlayingHighlight();
      setTimeout(() => {
        if (currentTrack.value) {
          highlightPlayingTrack(currentTrack.value);
        }
      }, 50);
    }
  },
  { immediate: true },
);

// Sync from % → fontSize
watch(fontSizePct, (newPct) => {
  const clamped = clamp(newPct, 20, 200);
  const em = parseFloat(((clamped / 100) * props.baseFontSize).toFixed(2));
  fontSize.value = em;
});

const reset = () => {
  fontSizePct.value = 100;
};
defineExpose({ reset });
</script>

<template>
  <v-list-item
    density="compact"
    data-testid="bp-setting-font-size"
    :title="t('label_font_size')"
    :area-label="t('label_font_size')"
  >
    <template #prepend>
      <FontAwesomeIcon :size="!smAndDown ? 'xl' : 'lg'" icon="fa-solid fa-text-size" class="pr-3" />
    </template>
    <template #append>
      <div class="d-flex align-center ml-4">
        <v-number-input
          v-model="fontSizePct"
          class="my-1"
          variant="outlined"
          density="compact"
          glow
          flat
          :min="20"
          :max="200"
          :step="10"
          :counter-value="3"
          inset
          control-variant="split"
          min-width="180"
          suffix="%"
          aria-live="polite"
          aria-atomic="true"
          :aria-label="t('book_player.label_current_font_size')"
          style="height: 40px"
        >
          <template #decrement="{ props }">
            <v-btn
              :disabled="fontSizePct === 20"
              v-bind="props"
              class="rounded-lg"
              variant="plain"
              icon="mdi-minus"
              color="on-secondary"
              :ripple="false"
              :aria-label="t('book_player.button_decrease_font_size')"
            />
          </template>
          <template #increment="{ props }">
            <v-btn
              :disabled="fontSizePct === 200"
              v-bind="props"
              class="rounded-lg"
              variant="plain"
              icon="mdi-plus"
              color="on-secondary"
              :ripple="false"
              :aria-label="t('book_player.button_increase_font_size')"
            />
          </template>
        </v-number-input>
      </div>
    </template>
  </v-list-item>
</template>

<style scoped lang="scss">
:deep(.v-text-field__suffix) {
  color: var(--v-theme-on-surface);
}
:deep(.v-icon) {
  opacity: 1 !important;
}
</style>
